<template>
    <b-modal
        id="hourly-usage-analysis-modal"
        :value="show"
        :title="getModalTitle()"
        size="xl"
        hide-footer
        centered
        @shown="loadUsageAnalysis"
        @hidden="handleHidden"
    >
        <div v-if="loadingUsageAnalysis" class="usage-analysis-loading">
            <i class="fas fa-spinner fa-spin"></i>
            <span>데이터를 불러오는 중...</span>
        </div>
        <div v-else-if="usageAnalysisData" class="usage-analysis-content">
            <!-- 요약 정보 -->
            <div class="usage-analysis-summary">
                <div class="summary-left">
                    <div class="summary-item">
                        <span class="summary-label">고객번호:</span>
                        <span class="summary-value">{{ custNo || "-" }}</span>
                    </div>
                    <div class="summary-item">
                        <span class="summary-label">계량기 번호:</span>
                        <span class="summary-value">{{ meterNo || "전체" }}</span>
                    </div>
                    <div class="summary-item">
                        <span class="summary-label">기간:</span>
                        <span class="summary-value">
                            {{
                                formatDateRange(usageAnalysisData.period?.startDate, usageAnalysisData.period?.endDate)
                            }}
                        </span>
                    </div>
                    <div class="summary-item">
                        <span class="summary-label">단위:</span>
                        <span class="summary-value">{{ usageAnalysisData.unit || "kWh" }}</span>
                    </div>
                    <div class="summary-item">
                        <span class="summary-label">{{ getExtremeValueLabel() }}:</span>
                        <span class="summary-value total">
                            {{ getExtremeValueDisplay() }}
                        </span>
                    </div>
                </div>
                <div class="summary-right">
                    <span class="data-type-label">데이터 유형 :</span>
                    <select v-model="dataType" @change="onDataTypeChange" class="data-type-select">
                        <option value="USAGE">사용량</option>
                        <option value="PEAK">피크</option>
                    </select>
                    <span class="sum-method-label">집계방법 :</span>
                    <select v-model="sumMethod" @change="onSumMethodChange" class="sum-method-select">
                        <option value="Sum">합계</option>
                        <option value="Avg">평균</option>
                        <option value="Max">최대</option>
                        <option value="Min">최소</option>
                    </select>
                    <button class="btn-excel-download" @click="exportToExcel" title="엑셀 다운로드">
                        <i class="fas fa-file-excel"></i>
                        엑셀 다운로드
                    </button>
                </div>
            </div>

            <!-- 범례 -->
            <div class="legend-container">
                <div class="legend-section">
                    <span class="legend-title">시간대별 요금:</span>
                    <div class="legend-items">
                        <div class="legend-item">
                            <span class="legend-box time-slot-light"></span>
                            <span class="legend-label">경부하 (23~09시)</span>
                        </div>
                        <div class="legend-item">
                            <span class="legend-box time-slot-mid"></span>
                            <span class="legend-label">중부하 (09~10, 12~13, 17~23시)</span>
                        </div>
                        <div class="legend-item">
                            <span class="legend-box time-slot-peak"></span>
                            <span class="legend-label">최대부하 (10~12, 13~17시)</span>
                        </div>
                    </div>
                </div>
                <div class="legend-section">
                    <span class="legend-title">사용량:</span>
                    <div class="heatmap-legend">
                        <span class="heatmap-label">낮음</span>
                        <div class="heatmap-gradient"></div>
                        <span class="heatmap-label">높음</span>
                    </div>
                </div>
            </div>

            <!-- 그리드 테이블 -->
            <div class="usage-grid-container">
                <table class="usage-grid-table">
                    <thead>
                        <tr>
                            <th class="hour-header">시간</th>
                            <th
                                v-for="(column, index) in usageAnalysisData.columns"
                                :key="index"
                                :class="[
                                    'date-header',
                                    {
                                        'saturday-header': column.dayOfWeek === '토',
                                        'sunday-header': column.dayOfWeek === '일',
                                        'header-highlighted': hoveredCol === index,
                                        'extreme-col': isExtremeCol(index),
                                    },
                                ]"
                            >
                                {{ column.month }}/{{ column.day }}({{ column.dayOfWeek }})
                            </th>
                            <th class="min-header">{{ getSumMethodLabel() }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(gridItem, rowIndex) in usageAnalysisData?.grid || []"
                            :key="gridItem.hour"
                            :class="[getTimeSlotClass(gridItem.hour), { 'extreme-row': isExtremeRow(rowIndex) }]"
                        >
                            <td
                                class="hour-cell"
                                :class="[
                                    getTimeSlotClass(gridItem.hour),
                                    { 'header-highlighted': hoveredRow === rowIndex },
                                ]"
                            >
                                {{ gridItem.hour }}시
                            </td>
                            <td
                                v-for="(value, colIndex) in gridItem.data"
                                :key="colIndex"
                                :class="[
                                    'value-cell',
                                    {
                                        'zero-value': value === 0,
                                        'saturday-cell':
                                            usageAnalysisData.columns &&
                                            usageAnalysisData.columns[colIndex]?.dayOfWeek === '토',
                                        'sunday-cell':
                                            usageAnalysisData.columns &&
                                            usageAnalysisData.columns[colIndex]?.dayOfWeek === '일',
                                        'extreme-value': isExtremeValue(rowIndex, colIndex, value),
                                        'extreme-col': isExtremeCol(colIndex),
                                    },
                                ]"
                                :style="getHeatmapStyle(value)"
                                @mouseenter="onCellHover(rowIndex, colIndex)"
                                @mouseleave="onCellLeave"
                            >
                                {{ value === 0 ? "-" : formatNumber(value, 1) }}
                            </td>
                            <td class="min-cell" @mouseenter="onCellHover(rowIndex, -2)" @mouseleave="onCellLeave">
                                {{ formatNumber(getAggregatedValue(gridItem) || 0, 1) }}
                            </td>
                        </tr>
                        <!-- 일별 집계값 행 -->
                        <tr
                            v-if="usageAnalysisData.grid"
                            :class="['summary-row', 'daily-min-row', { 'extreme-row': isExtremeRow(-1) }]"
                        >
                            <td class="hour-cell summary-label">{{ getSumMethodLabel() }}</td>
                            <td
                                v-for="(col, colIndex) in usageAnalysisData.columns"
                                :key="`agg-${colIndex}`"
                                :class="['summary-cell', 'daily-min-cell', { 'extreme-col': isExtremeCol(colIndex) }]"
                                @mouseenter="onCellHover(-2, colIndex)"
                                @mouseleave="onCellLeave"
                            >
                                {{ formatNumber(getDailyAggregatedValue(colIndex) || 0, 1) }}
                            </td>
                            <td class="summary-cell min-total-cell">
                                {{ formatNumber(getOverallAggregatedValue() || 0, 1) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div v-else class="usage-analysis-empty">
            <i class="fas fa-exclamation-circle"></i>
            <span>데이터를 불러올 수 없습니다.</span>
        </div>
    </b-modal>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import * as XLSX from "xlsx";

export default {
    name: "HourlyUsageAnalysisModal",
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        regDt: {
            type: String,
            required: true,
        },
        custNo: {
            type: String,
            required: true,
        },
        meterNo: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            usageAnalysisData: null,
            loadingUsageAnalysis: false,
            hoveredRow: null,
            hoveredCol: null,
            dataType: "USAGE", // 기본값: 사용량
            sumMethod: "Sum", // 기본값: 합계
        };
    },
    computed: {
        maxValue() {
            if (!this.usageAnalysisData?.grid) return 0;
            let max = 0;
            this.usageAnalysisData.grid.forEach((row) => {
                row.data.forEach((value) => {
                    if (value > max) max = value;
                });
            });
            return max;
        },
        minValue() {
            if (!this.usageAnalysisData?.grid) return 0;
            let min = Infinity;
            this.usageAnalysisData.grid.forEach((row) => {
                row.data.forEach((value) => {
                    if (value > 0 && value < min) min = value;
                });
            });
            return min === Infinity ? 0 : min;
        },
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.$bvModal.show("hourly-usage-analysis-modal");
                });
            } else {
                this.$bvModal.hide("hourly-usage-analysis-modal");
            }
        },
    },
    mounted() {
        if (this.show) {
            this.$nextTick(() => {
                this.$bvModal.show("hourly-usage-analysis-modal");
            });
        }
    },
    methods: {
        getExtremeValueInfo() {
            // 전체 그리드에서 극단값과 그 위치 찾기
            if (!this.usageAnalysisData?.grid) {
                return null;
            }

            const isFindMax = this.sumMethod === "Sum" || this.sumMethod === "Avg" || this.sumMethod === "Max";

            // 전체 그리드에서 극단값 찾기 (0 제외)
            const allValues = [];
            this.usageAnalysisData.grid.forEach((row) => {
                row.data.forEach((v) => {
                    if (v > 0) allValues.push(v);
                });
            });

            if (allValues.length === 0) return null;

            // 전체 그리드의 극단값 계산
            const globalExtreme = isFindMax ? Math.max(...allValues) : Math.min(...allValues);

            // 극단값이 있는 첫 번째 셀의 위치 찾기
            for (let rowIndex = 0; rowIndex < this.usageAnalysisData.grid.length; rowIndex++) {
                const row = this.usageAnalysisData.grid[rowIndex];
                for (let colIndex = 0; colIndex < row.data.length; colIndex++) {
                    const value = row.data[colIndex];
                    if (value === globalExtreme && this.isExtremeValue(rowIndex, colIndex, value)) {
                        // 시간 정보 (rowIndex는 시간)
                        const hour = row.hour;

                        // 날짜 정보 (colIndex는 날짜)
                        const column = this.usageAnalysisData.columns?.[colIndex];
                        const dateStr = column ? `${column.month}/${column.day}(${column.dayOfWeek})` : "";

                        return {
                            value: globalExtreme,
                            hour: hour,
                            date: dateStr,
                        };
                    }
                }
            }

            return null;
        },
        getExtremeValueLabel() {
            const isFindMax = this.sumMethod === "Sum" || this.sumMethod === "Avg" || this.sumMethod === "Max";
            return isFindMax ? "최대구간" : "최소구간";
        },
        getExtremeValueDisplay() {
            const info = this.getExtremeValueInfo();
            if (!info) {
                // 극단값을 찾을 수 없으면 기존 방식으로 표시
                return `${this.formatNumber(this.getOverallAggregatedValue() || 0, 1)} ${this.usageAnalysisData?.unit || "kWh"}`;
            }

            // 표시 형식: "날짜 시간 (극단값)"
            return `${info.date} ${info.hour}시 (${this.formatNumber(info.value, 1)} ${this.usageAnalysisData?.unit || "kWh"})`;
        },
        getDataTypeLabel() {
            return this.dataType === "PEAK" ? "피크" : "사용량";
        },
        getModalTitle() {
            return `데이터 분석 (${this.getDataTypeLabel()})`;
        },
        isExtremeValue(rowIndex, colIndex, value) {
            if (!this.usageAnalysisData?.grid || value === 0 || value === null || value === undefined) {
                return false;
            }

            // 집계방법에 따라 극단값 찾기 방식 결정
            // 합계, 평균, 최대: 최대값 찾기
            // 최소: 최소값 찾기
            const isFindMax = this.sumMethod === "Sum" || this.sumMethod === "Avg" || this.sumMethod === "Max";

            // 전체 그리드에서 극단값 찾기 (0 제외)
            const allValues = [];
            this.usageAnalysisData.grid.forEach((row) => {
                row.data.forEach((v) => {
                    if (v > 0) allValues.push(v);
                });
            });

            if (allValues.length === 0) return false;

            // 전체 그리드의 극단값 계산
            const globalExtreme = isFindMax ? Math.max(...allValues) : Math.min(...allValues);

            // 현재 셀의 값이 전체 그리드의 극단값과 일치하는지 확인
            // 그리고 해당 행과 열 모두에서 극단값인지 확인
            if (value !== globalExtreme) {
                return false;
            }

            // 해당 행의 모든 값 중 극단점 찾기 (0 제외)
            const rowValues = this.usageAnalysisData.grid[rowIndex].data.filter((v) => v > 0);
            if (rowValues.length === 0) return false;

            // 해당 열의 모든 값 중 극단점 찾기 (0 제외)
            const colValues = this.usageAnalysisData.grid.map((row) => row.data[colIndex]).filter((v) => v > 0);
            if (colValues.length === 0) return false;

            // 행과 열의 극단값 계산
            const rowExtreme = isFindMax ? Math.max(...rowValues) : Math.min(...rowValues);
            const colExtreme = isFindMax ? Math.max(...colValues) : Math.min(...colValues);

            // 현재 셀의 값이 행의 극단값이면서 동시에 열의 극단값인지 확인
            // 그리고 전체 그리드의 극단값과도 일치해야 함
            const isRowExtreme = value === rowExtreme;
            const isColExtreme = value === colExtreme;

            // 전체 그리드의 극단값이면서, 행과 열 모두에서도 극단값인 경우에만 true
            return isRowExtreme && isColExtreme;
        },
        isExtremeRow(rowIndex) {
            if (!this.usageAnalysisData?.grid) {
                return false;
            }

            // rowIndex가 -1이면 집계행 체크
            if (rowIndex === -1) {
                // 집계행은 항상 false (집계행 자체는 강조하지 않음)
                return false;
            }

            // rowIndex가 유효한 범위인지 확인
            if (rowIndex < 0 || rowIndex >= this.usageAnalysisData.grid.length) {
                return false;
            }

            const row = this.usageAnalysisData.grid[rowIndex];
            if (!row || !row.data) {
                return false;
            }

            // 해당 행에 극단값이 교차하는 셀이 있는지 확인
            // 즉, 행과 열 모두에서 극단값인 셀이 있는 행만 강조
            for (let colIndex = 0; colIndex < row.data.length; colIndex++) {
                const value = row.data[colIndex];
                if (this.isExtremeValue(rowIndex, colIndex, value)) {
                    return true;
                }
            }
            return false;
        },
        isExtremeCol(colIndex) {
            if (!this.usageAnalysisData?.grid) {
                return false;
            }

            // colIndex가 유효한 범위인지 확인
            if (
                colIndex < 0 ||
                !this.usageAnalysisData.grid[0] ||
                colIndex >= this.usageAnalysisData.grid[0].data.length
            ) {
                return false;
            }

            // 해당 열에 극단값이 교차하는 셀이 있는지 확인
            // 즉, 행과 열 모두에서 극단값인 셀이 있는 열만 강조
            for (let rowIndex = 0; rowIndex < this.usageAnalysisData.grid.length; rowIndex++) {
                const row = this.usageAnalysisData.grid[rowIndex];
                if (!row || !row.data) {
                    continue;
                }
                const value = row.data[colIndex];
                if (this.isExtremeValue(rowIndex, colIndex, value)) {
                    return true;
                }
            }
            return false;
        },
        async loadUsageAnalysis() {
            if (!this.custNo) {
                this.$bvToast.toast("고객번호가 없습니다.", {
                    title: "오류",
                    variant: "danger",
                    solid: true,
                });
                return;
            }

            this.loadingUsageAnalysis = true;
            this.usageAnalysisData = null;

            try {
                const result = await backEndApi.kepco.getHourlyUsageGrid(
                    this.regDt,
                    this.custNo,
                    this.meterNo,
                    this.sumMethod,
                    this.dataType
                );

                if (result && result.status === 200 && result.data && result.data.success) {
                    // API 응답 데이터를 컴포넌트가 기대하는 형식으로 변환
                    const apiData = result.data.data;
                    this.usageAnalysisData = {
                        period: apiData.period,
                        columns: apiData.columns,
                        grid: apiData.grid,
                        summary: apiData.summary,
                        unit: apiData.unit || "kWh",
                        timeSlotInfo: apiData.timeSlotInfo || null,
                    };
                } else {
                    throw new Error("데이터를 불러올 수 없습니다.");
                }
            } catch (error) {
                console.error("Failed to load usage analysis:", error);
                this.$bvToast.toast("시간대별 사용량 분석 데이터를 불러오는데 실패했습니다.", {
                    title: "오류",
                    variant: "danger",
                    solid: true,
                });
                this.usageAnalysisData = null;
            } finally {
                this.loadingUsageAnalysis = false;
            }
        },
        formatDateRange(startDate, endDate) {
            if (!startDate || !endDate) return "-";
            const formatDate = (dateStr) => {
                if (dateStr.length === 8) {
                    return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
                }
                return dateStr;
            };
            return `${formatDate(String(startDate))} ~ ${formatDate(String(endDate))}`;
        },
        formatNumber(value, decimals = 0) {
            if (value === null || value === undefined || isNaN(value)) return "0";
            const num = Number(value);
            // 큰 숫자는 축약 표시
            if (num >= 10000) {
                return (num / 1000).toFixed(decimals) + "k";
            }
            return num.toLocaleString("ko-KR", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            });
        },
        handleHidden() {
            this.$emit("update:show", false);
        },
        onCellHover(rowIndex, colIndex) {
            this.hoveredRow = rowIndex;
            this.hoveredCol = colIndex;
        },
        onCellLeave() {
            this.hoveredRow = null;
            this.hoveredCol = null;
        },
        getTimeSlotClass(hour) {
            // API의 timeSlotInfo를 사용하여 시간대 구분
            if (!this.usageAnalysisData?.timeSlotInfo) {
                // timeSlotInfo가 없으면 기본값 반환 (하위 호환성)
                if ((hour >= 23 && hour <= 24) || (hour >= 0 && hour < 9)) {
                    return "time-slot-light";
                } else if ((hour >= 9 && hour < 10) || (hour >= 12 && hour < 13) || (hour >= 17 && hour < 23)) {
                    return "time-slot-mid";
                } else if ((hour >= 10 && hour < 12) || (hour >= 13 && hour < 17)) {
                    return "time-slot-peak";
                }
                return "";
            }

            // timeSlotInfo에서 해당 시간대 찾기
            const timeSlot = this.usageAnalysisData.timeSlotInfo.find((item) => {
                const itemHour = typeof item.hour === "number" ? item.hour : parseInt(item.hour);
                return itemHour === hour;
            });

            if (!timeSlot) {
                return "time-slot-light"; // 기본값: 경부하
            }

            // loadType에 따라 클래스 반환
            const loadType = timeSlot.loadType || timeSlot.LoadType;
            if (loadType === "lowPeak" || loadType === "LowPeak") {
                return "time-slot-light";
            } else if (loadType === "midPeak" || loadType === "MidPeak") {
                return "time-slot-mid";
            } else if (loadType === "highPeak" || loadType === "HighPeak") {
                return "time-slot-peak";
            }

            return "time-slot-light"; // 기본값
        },
        getHeatmapStyle(value) {
            if (value === 0 || !value) {
                return {};
            }

            const range = this.maxValue - this.minValue;
            if (range === 0) return {};

            // 값의 비율 계산 (0~1)
            const ratio = (value - this.minValue) / range;

            // 붉은 계열 색상 팔레트 사용
            // 낮은 값: 연한 빨간색 (#fee2e2), 높은 값: 진한 빨간색 (#dc2626)
            const baseColor = { r: 220, g: 38, b: 38 }; // #dc2626
            const lightColor = { r: 254, g: 226, b: 226 }; // #fee2e2

            const r = Math.round(lightColor.r + (baseColor.r - lightColor.r) * ratio);
            const g = Math.round(lightColor.g + (baseColor.g - lightColor.g) * ratio);
            const b = Math.round(lightColor.b + (baseColor.b - lightColor.b) * ratio);

            // 텍스트 색상: 진한 배경일 때는 흰색, 연한 배경일 때는 검은색
            const textColor = ratio > 0.5 ? "#ffffff" : "#1e293b";

            return {
                backgroundColor: `rgb(${r}, ${g}, ${b})`,
                color: textColor,
                fontWeight: "normal",
            };
        },
        getOverallMin() {
            if (!this.usageAnalysisData?.summary?.dailyMinByHour) return 0;
            return Math.min(...this.usageAnalysisData.summary.dailyMinByHour.filter((v) => v > 0));
        },
        onSumMethodChange() {
            // 집계방법 변경 시 데이터 재로드
            this.loadUsageAnalysis();
        },
        onDataTypeChange() {
            // 데이터 유형 변경 시 데이터 재로드
            this.loadUsageAnalysis();
        },
        getSumMethodLabel() {
            const labels = {
                Sum: "합계",
                Avg: "평균",
                Max: "최대",
                Min: "최소",
            };
            return labels[this.sumMethod] || "합계";
        },
        getAggregatedValue(gridItem) {
            // total 필드에 집계방법에 따라 계산된 값이 저장됨
            return gridItem.total || 0;
        },
        getDailyAggregatedValue(colIndex) {
            if (!this.usageAnalysisData?.grid) return 0;

            // 각 행의 해당 컬럼(날짜)의 값들을 집계
            const values = this.usageAnalysisData.grid.map((row) => row.data[colIndex]).filter((val) => val > 0);

            if (values.length === 0) return 0;

            // 일별 집계는 선택된 집계방법에 따라 계산
            if (this.sumMethod === "Sum") {
                return values.reduce((sum, val) => sum + val, 0);
            } else if (this.sumMethod === "Avg") {
                return values.reduce((sum, val) => sum + val, 0) / values.length;
            } else if (this.sumMethod === "Max") {
                return Math.max(...values);
            } else if (this.sumMethod === "Min") {
                return Math.min(...values);
            }
            return 0;
        },
        getOverallAggregatedValue() {
            // Backend에서 계산한 grandTotal을 직접 사용
            return this.usageAnalysisData?.summary?.grandTotal || 0;
        },
        exportToExcel() {
            if (!this.usageAnalysisData) {
                this.$bvToast.toast("다운로드할 데이터가 없습니다.", {
                    title: "오류",
                    variant: "danger",
                    solid: true,
                });
                return;
            }

            try {
                const data = this.usageAnalysisData;
                const excelData = [];

                // 헤더 행 생성
                const headerRow = ["시간"];
                if (data.columns && data.columns.length > 0) {
                    data.columns.forEach((column) => {
                        headerRow.push(`${column.month}/${column.day}(${column.dayOfWeek})`);
                    });
                }
                headerRow.push("합계", "최소");
                excelData.push(headerRow);

                // 시간대별 데이터 행
                if (data.grid && data.grid.length > 0) {
                    data.grid.forEach((gridItem) => {
                        const row = [`${gridItem.hour}시`];
                        if (gridItem.data && gridItem.data.length > 0) {
                            gridItem.data.forEach((value) => {
                                row.push(value === 0 ? "-" : Number(value));
                            });
                        }
                        row.push(Number(gridItem.total || 0), Number(gridItem.min || 0));
                        excelData.push(row);
                    });
                }

                // 일별 합계 행
                if (data.summary && data.summary.dailyTotals) {
                    const totalsRow = ["일별 합계"];
                    data.summary.dailyTotals.forEach((total) => {
                        totalsRow.push(Number(total));
                    });
                    totalsRow.push(Number(data.summary.grandTotal || 0), "-");
                    excelData.push(totalsRow);
                }

                // 일별 최소값 행
                if (data.summary && data.summary.dailyMinByHour) {
                    const minRow = ["일별 최소"];
                    data.summary.dailyMinByHour.forEach((min) => {
                        minRow.push(Number(min));
                    });
                    minRow.push("-", Number(this.getOverallMin()));
                    excelData.push(minRow);
                }

                // 워크시트 생성
                const worksheet = XLSX.utils.aoa_to_sheet(excelData);

                // 컬럼 너비 설정
                const columnWidths = [{ wch: 10 }]; // 시간 컬럼
                if (data.columns) {
                    data.columns.forEach(() => {
                        columnWidths.push({ wch: 12 }); // 날짜 컬럼들
                    });
                }
                columnWidths.push({ wch: 12 }, { wch: 12 }); // 합계, 최소 컬럼
                worksheet["!cols"] = columnWidths;

                // 워크북 생성
                const workbook = XLSX.utils.book_new();
                const sheetName = `시간대별사용량분석_${this.custNo}_${this.meterNo || "전체"}`;
                const finalSheetName = sheetName.length > 31 ? sheetName.substring(0, 31) : sheetName;
                XLSX.utils.book_append_sheet(workbook, worksheet, finalSheetName);

                // 파일명 생성
                const today = new Date();
                const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;
                const fileName = `시간대별사용량분석_${this.custNo}_${this.meterNo || "전체"}_${dateStr}.xlsx`;

                // 엑셀 파일 다운로드
                XLSX.writeFile(workbook, fileName);

                this.$bvToast.toast("엑셀 파일이 다운로드되었습니다.", {
                    title: "완료",
                    variant: "success",
                    solid: true,
                });
            } catch (error) {
                console.error("Failed to export to Excel:", error);
                this.$bvToast.toast("엑셀 다운로드 중 오류가 발생했습니다.", {
                    title: "오류",
                    variant: "danger",
                    solid: true,
                });
            }
        },
    },
};
</script>

<style scoped>
::v-deep #hourly-usage-analysis-modal .modal-dialog {
    width: 98% !important;
    max-width: 98% !important;
}

::v-deep #hourly-usage-analysis-modal .modal-content {
    max-height: 95vh;
}

.usage-analysis-loading,
.usage-analysis-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #64748b;
    gap: 12px;
    height: 95vh;
}

.usage-analysis-loading i {
    font-size: 24px;
    color: #3b82f6;
}

.usage-analysis-empty i {
    font-size: 24px;
    color: #94a3b8;
}

.usage-analysis-content {
    display: flex;
    flex-direction: column;
    gap: 10px;
    height: 85vh;
}

.usage-analysis-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 8px 12px;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

.summary-left {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    flex: 1;
}

.summary-right {
    display: flex;
    align-items: center;
    gap: 12px;
}

.data-type-label {
    font-size: 13px;
    font-weight: normal;
    color: #1e293b;
    white-space: nowrap;
    margin-right: 4px;
}

.data-type-select {
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    cursor: pointer;
    outline: none;
    margin-right: 12px;
    min-width: 100px;
}

.data-type-select:hover {
    border-color: #cbd5e1;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.data-type-select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.sum-method-label {
    font-size: 13px;
    font-weight: normal;
    color: #1e293b;
    white-space: nowrap;
    margin-right: 4px;
}

.sum-method-select {
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    cursor: pointer;
    outline: none;
    transition: all 0.2s;
}

.sum-method-select:hover {
    border-color: #cbd5e1;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.sum-method-select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.summary-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
}

.summary-label {
    font-weight: bold;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
}

.summary-value {
    color: #1e293b;
    font-weight: bold;
}

.summary-value.total {
    color: #1e293b;
    font-weight: bold;
}

/* 범례 스타일 */
.legend-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    gap: 24px;
    flex-wrap: wrap;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.legend-section {
    display: flex;
    align-items: center;
    gap: 12px;
}

.legend-title {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    white-space: nowrap;
}

.legend-items {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
}

.legend-box {
    width: 20px;
    height: 12px;
    border-radius: 3px;
    border: 1px solid #e2e8f0;
}

.legend-label {
    font-size: 10px;
    color: #64748b;
    white-space: nowrap;
}

.heatmap-legend {
    display: flex;
    align-items: center;
    gap: 8px;
}

.heatmap-gradient {
    width: 120px;
    height: 12px;
    background: linear-gradient(to right, #fee2e2, #dc2626);
    border-radius: 3px;
    border: 1px solid #e2e8f0;
}

.heatmap-label {
    font-size: 10px;
    color: #64748b;
}

.usage-grid-container {
    overflow-y: auto;
    height: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    width: 100%;
}

.usage-grid-table {
    width: 100%;
    height: 100%;
    border-collapse: collapse;
    font-size: 9px;
    background: white;
    table-layout: fixed;
}

.usage-grid-table thead {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #d9d9d9;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.usage-grid-table th {
    padding: 6px 1px;
    text-align: center;
    font-weight: 800;
    color: black;
    border: 1px solid #c0c0c0;
    background: #d9d9d9;
    white-space: nowrap;
    font-size: 10px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.hour-header {
    position: sticky;
    left: 0;
    z-index: 11;
    background: #d9d9d9;
    color: black;
    width: 35px;
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
}

.total-header {
    background: #d9d9d9;
    color: black;
    width: 50px;
    font-weight: 800;
    box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
}

.min-header {
    background: #d9d9d9;
    color: black;
    width: 50px;
    font-weight: 800;
    box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
}

.date-header {
    width: auto;
    padding: 4px 2px !important;
}

.saturday-header {
    background: #e3f2fd !important;
    color: #1565c0 !important;
}

.sunday-header {
    background: #ffebee !important;
    color: #c62828 !important;
}

.usage-grid-table tbody tr {
    border-bottom: 1px solid #c0c0c0;
}

.header-highlighted {
    background: rgba(59, 130, 246, 0.15) !important;
    font-weight: 800;
}

.usage-grid-table td {
    padding: 3px 2px;
    text-align: right;
    border: 1px solid #c0c0c0;
    color: #1e293b;
    font-size: 11px;
    font-weight: normal;
    overflow: hidden;
    text-overflow: ellipsis;
}

.hour-cell {
    position: sticky;
    left: 0;
    z-index: 9;
    background: #d9d9d9;
    text-align: center;
    font-weight: 700;
    color: black;
    width: 35px;
    padding: 3px 1px;
    font-size: 9px;
    border: 1px solid #c0c0c0;
}

.value-cell {
    font-size: 9px;
    font-weight: normal;
    position: relative;
    cursor: pointer;
}

.value-cell.zero-value {
    color: #cbd5e1;
    font-style: italic;
    background: #f8fafc !important;
}

/* 극단값이 있는 행 전체 강조 - 열 강조와 동일한 방식 */
.usage-grid-table tbody tr.extreme-row {
    border-top: none;
    border-bottom: none;
}

/* 극단값 행의 모든 셀(시간 열, 데이터 셀, 집계 열 포함)에 상하 테두리 적용 */
.usage-grid-table tbody tr.extreme-row td {
    border-top: 2px solid black !important;
    border-bottom: 2px solid black !important;
    font-weight: bold !important;
}

/* 극단값 행의 첫 번째 셀(시간 셀) - 왼쪽 테두리 추가 */
.usage-grid-table tbody tr.extreme-row td.hour-cell {
    border-left: 2px solid black !important;
}

/* 극단값 행의 마지막 셀(집계 셀) - 오른쪽 테두리 추가 */
.usage-grid-table tbody tr.extreme-row td.min-cell,
.usage-grid-table tbody tr.extreme-row td.min-total-cell {
    border-right: 2px solid black !important;
}

/* 극단값 행의 첫 번째 데이터 행 - 상단 테두리 제거 */
.usage-grid-table tbody tr.extreme-row:first-child td {
    border-top: none !important;
}

/* 집계행의 극단값 행 셀 - 하단 테두리 추가 */
.usage-grid-table tbody tr.extreme-row.summary-row td,
.usage-grid-table tbody tr.extreme-row.daily-min-row td {
    border-bottom: 2px solid black !important;
}

/* 극단값이 있는 열 전체 강조 - 완전한 사각형 테두리 */
.usage-grid-table thead th.extreme-col {
    border-top: 2px solid black !important;
    border-left: 2px solid black !important;
    border-right: 2px solid black !important;
    border-bottom: none !important;
}

/* 극단값 열의 모든 셀에 좌우 테두리 적용 */
.usage-grid-table tbody tr td.extreme-col {
    border-left: 2px solid black !important;
    border-right: 2px solid black !important;
    position: relative;
    font-weight: bold !important;
}

/* 극단값 열의 첫 번째 셀(헤더 바로 아래) - 상단 테두리 제거하여 헤더와 연결 */
.usage-grid-table tbody tr:first-child td.extreme-col {
    border-top: none !important;
}

/* 극단값 열의 마지막 셀(집계행) - 하단 테두리 추가하여 완전한 사각형 완성 */
.usage-grid-table tbody tr:last-child td.extreme-col {
    border-bottom: 2px solid black !important;
}

/* 집계행의 극단값 열 셀도 포함 */
.usage-grid-table tbody tr.summary-row td.extreme-col,
.usage-grid-table tbody tr.daily-min-row td.extreme-col,
.usage-grid-table tbody tr.daily-totals-row td.extreme-col {
    border-bottom: 2px solid black !important;
}

/* 극단값 셀 자체 강조 (행과 열 교차점) */
.value-cell.extreme-value {
    border: 2px solid black !important;
    background: lime !important;
    color: black !important;
    font-weight: bold !important;
    position: relative;
    z-index: 10;
}

/* 시간대별 색상 (좌측 표시) */
.time-slot-light {
    border-left: 3px solid #10b981 !important;
}

.time-slot-mid {
    border-left: 3px solid #f59e0b !important;
}

.time-slot-peak {
    border-left: 3px solid #ef4444 !important;
}

.total-cell {
    background: #d9d9d9;
    color: black;
    font-weight: 800;
    text-align: right;
    width: 50px;
    padding: 3px 2px;
    font-size: 9px;
    border: 1px solid #c0c0c0;
    position: sticky;
    right: 50px;
    z-index: 8;
    box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
}

.min-cell {
    background: #d9d9d9;
    color: black;
    font-weight: 800;
    text-align: right;
    width: 50px;
    padding: 3px 2px;
    font-size: 9px;
    border: 1px solid #c0c0c0;
    position: sticky;
    right: 0;
    z-index: 8;
    box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
}

/* Summary 행 스타일 */
.summary-row {
    background: #d9d9d9;
    position: sticky;
    bottom: 0;
    z-index: 7;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
}

.summary-row:hover {
    background: #d9d9d9;
}

.summary-label {
    font-weight: 800;
    color: black;
    background: transparent !important;
    border: none !important;
}

.summary-cell {
    font-weight: 800;
    text-align: right;
    font-size: 9px;
    color: black;
    background: #d9d9d9;
    border: 1px solid #c0c0c0;
}

.daily-totals-row {
    background: #d9d9d9;
}

.daily-totals-row .summary-label {
    background: transparent !important;
    font-weight: 800;
    color: black;
    border: none !important;
}

.daily-total-cell {
    background: #d9d9d9;
    color: black;
    font-weight: 800;
    border: 1px solid #c0c0c0;
}

.grand-total-cell {
    background: #d9d9d9;
    color: black;
    font-weight: 900;
    border: 1px solid #c0c0c0;
    position: sticky;
    right: 50px;
    z-index: 8;
    box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
}

.daily-min-row {
    background: #d9d9d9;
}

.daily-min-row .summary-label {
    background: transparent !important;
    font-weight: 800;
    color: black;
    border: none !important;
}

.daily-min-cell {
    background: #d9d9d9;
    color: black;
    font-weight: 800;
    border: 1px solid #c0c0c0;
}

.min-total-cell {
    background: #d9d9d9;
    color: black;
    font-weight: 900;
    border: 1px solid #c0c0c0;
    position: sticky;
    right: 0;
    z-index: 8;
    box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
}

/* 엑셀 다운로드 버튼 */
.btn-excel-download {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px;
    font-size: 11px;
    font-weight: 600;
    color: #ffffff;
    background: #107c41;
    border: 1px solid #107c41;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-excel-download:hover {
    background: #0d6b35;
    border-color: #0d6b35;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(16, 124, 65, 0.3);
}

.btn-excel-download:active {
    transform: translateY(0);
}

.btn-excel-download i {
    font-size: 11px;
}
</style>
